<template>
  <div class="Theater theme-dk">

    <div class="Theater-stage">

      <vimeo class="Theater-activeItem"
        :key="activeItem.hash"
        v-if="activeItem.type === 'vimeo'"
        v-bind="activeItem" />

      <interactive-thumbnail class="Theater-activeItem"
        v-if="activeItem.type === 'interactive'"
        v-bind="activeItem" />

      <smugmug-album-thumbnail class="Theater-activeItem Theater-smugmugAlbumThumbnail"
        v-if="activeItem.type === 'smugmug'"
        :album-key="activeItem['smugmug-album']"
        v-bind="activeItem"
        :route="{}"
        @click.native="openViewer(activeItem)"
        large />

    </div>

    <div class="Theater-footer">

      <div class="Theater-info">

        <template v-if="activeItem.title || activeItem.description">

          <h1 class="t-heading-sm c-grey-xxlt"
            v-if="activeItem.title">
            {{ activeItem.title }}
          </h1>

          <break size="0.25" />

          <div class="Theater-description"
            v-if="activeItem.description"
            v-html="activeItem.description">
          </div>

<!--          <div class="t-text-sm c-grey-xxlt"
            v-if="activeItem.description"
            v-html="activeItem.description">
          </div>
 -->
          

        </template>

      </div>

      <div class="Theater-share">

        <share-button class="Theater-shareButton">

          <share-tab />

        </share-button>

      </div>

      <div class="Theater-selector"
        v-if="data.length > 1">

        <div class="Theater-selectorArrow Theater-selectorArrowPrevious"
          @click="previous">

          <viewer-button
            icon="arrow-left"
            small />

        </div>

        <div class="Theater-selectorSlider"
          ref="slider">

          <div class="Theater-selectorItem"
            v-for="item in data"
            :class="{
              'Theater-selectorItem--isActive': item.hash === activeItem.hash
            }">

            <vimeo-thumbnail class="Theater-selectorItemThumbnail"
              v-if="item.type === 'vimeo'"
              :id="item.id"
              :image="item.image"
              :show-tag="false" />

          </div>

        </div>

        <div class="Theater-selectorArrow Theater-selectorArrowNext"
          @click="next">

          <viewer-button
            icon="arrow-right"
            small />

        </div>

      </div>

    </div>

  </div>
</template>

<script>
import Flickity from 'flickity'
import { find, findIndex } from 'lodash'

export default {
  name: 'Theater',

  props: {
    /**
     * Array of items in the series
     */
    data: {
      type: Array,
      required: true
    },

    breadcrumbs: {
      type: Array
    }
  },

  data () {
    return {
      activeItemImage: false
    }
  },

  /**
   * Update meta tags depending on active item
   * - This updates the title and open graph tags
   * - OG tag updates must happen in order for facebook shares to work
   */
  metaInfo () {
    const info = {
      meta: [
        {
          vmid: 'og:url',
          property: 'og:url',
          content: `${this.site.baseUrl}${this.$route.path}?feature=${this.activeItem.hash}`
        },
        {
          vmid: 'og:title',
          property: 'og:title',
          content: this.activeItem.title || this.currentPage.attributes.title || this.site.title
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: this.activeItem.description || this.currentPage.attributes.description || this.currentPage.attributes.excerpt
        }
      ]
    }

    if (this.activeItemImage) {
      info.meta.push({
        vmid: 'og:image',
        property: 'og:image',
        content: this.$smugmug.resolveTemplate(this.activeItemImage, 1200)
      })

      info.meta.push({
        vmid: 'og:image:width',
        property: 'og:image:width',
        content: 1200
      })

      info.meta.push({
        vmid: 'og:image:height',
        property: 'og:image:height',
        content: this.$smugmug.getHeight(this.activeItemImage, 1200)
      })
    }

    return info
  },

  computed: {
    site () { return this.$store.state.data.site },
    currentPage () { return this.$store.state.currentPage },

    /**
     * Current active item
     * @return {Object}
     */
    activeItem () {
      // If there is only one item in this.data, return that
      if (this.data.length === 1) return this.data[0]

      // If there is no selected feature, use the first feature
      if (!this.$route.query.feature) return this.data[0]

      // Try to match the hash to an item
      return find(this.data, ['hash', this.$route.query.feature.replace(/\/$/, '')])
    }

    /**
     * Whether to show the footer element
     * @return {Boolean}
     */
    // showFooter () {
    //   // If there is more than 1 item
    //   if (this.data.length > 1) return true
    //
    //   if (this.activeItem.title || this.activeItem.description) return true
    //
    //   return true
    // }
  },

  watch: {
    activeItem () {
      this.updateShareData()
      this.updateActiveItemImage()
    }
  },

  mounted () {
    this.$nextTick(this.init)
  },

  beforeDestroy () {
    this.removeEventListeners()
  },

  methods: {
    /**
     * Initialize Flickity
     */
    init () {
      this.updateShareData()
      this.updateActiveItemImage()

      if (!this.$refs.slider) return

      this.flickity = new Flickity(this.$refs.slider, {
        prevNextButtons: false,
        cellSelector: '.Theater-selectorItem',
        cellAlign: 'left',
        contain: true
      })

      this.flickity.on('staticClick', this.handleStaticClick)

      this.flickity.select(findIndex(this.data, this.activeItem), false, true)

      // Handle select only after the first select
      this.flickity.on('select', this.handleSelect)
    },

    /**
     * Handle selector item static click
     */
    handleStaticClick (event, pointer, cellElement, cellIndex) {
      this.flickity.select(cellIndex)
    },

    /**
     * Handle Flickity selected event
     */
    handleSelect () {
      const selected = this.data[this.flickity.selectedIndex]
      this.$router.replace({ query: { feature: selected.hash } })
    },

    /**
     * Remove event listeners
     */
    removeEventListeners () {
      if (!this.flickity) return
      this.flickity.off('staticClick', this.handleStaticClick)
    },

    /**
     * Update the active item image, used for page meta update
     */
    async updateActiveItemImage () {
      if (!this.activeItem.image || !this.activeItem.image.smugmug) return
      this.activeItemImage = await this.$smugmug.get(this.activeItem.image.smugmug)
    },

    /**
     * Update share data
     */
    updateShareData () {
      this.$store.dispatch('share/setData', {
        title: this.activeItem.title || false,
        vimeoId: this.activeItem.type === 'vimeo' ? this.activeItem.id : false
      })
    },

    /**
     * Go to previous flickity item
     */
    previous () {
      this.flickity.previous()
    },

    /**
     * Go to next flickity item
     */
    next () {
      this.flickity.next()
    },

    /**
     * SmugmugAlbumThumbnail types have the option of opening the viewer
     * @return {item}
     */
    async openViewer (item) {
      const album = await this.$smugmug.getAlbum(item['smugmug-album'])
      // const breadcrumbs = concat(this.breadcrumbs, { title: album.attributes.title })

      this.$store.dispatch('viewer/open', {
        items: album.images,
        index: 0,
        breadcrumbs: this.breadcrumbs,
        gallery: item.route
      })
    }
  }
}
</script>

<style>
@import 'flickity/dist/flickity.css';
@import 'properties';

.Theater {

  /*
  TODO: apply only when there is more than one item, or a title or description is present
   */
  @media (min-width: 800px) {
    padding-bottom: 48px;
  }
}

.Theater-stage {

}

.Theater-stage .Thumbnail-tags {
  @media (min-width: 400px) {
    padding: 24px;
  }
}

.Theater-stage .Vimeo-button {
  @media (min-width: 400px) {
    bottom: 24px;
    left: 24px;
  }
}

.Theater-footer {
  margin-top: 19px;

  @media (min-width: 800px) and (min-height: 420px) {
    display: grid;
    grid-template-columns: 1fr 33.3333%;
    grid-column-gap: var(--unit);
    grid-row-gap: var(--unit);
  }
}

.Theater-info {
  margin: 0 0 var(--unit-05);
  padding: 0 var(--unit);

  @media (min-width: 960px) {
    margin: 0;
    padding: 0;
  }
}

.Theater-share {
  position: relative;
  z-index: 800;
  display: flex;
  justify-content: center;
  padding: 0 0 var(--unit-05);

  @media (min-width: 800px) and (min-height: 420px)  {
    display: none;
  }
}

/* When smugmug-album-thumbnail doesn't have a 'route' passed
it tries to make the cursor: default. */
.Theater-smugmugAlbumThumbnail > * {
  cursor: pointer !important;
}

.Theater-selector {

  @media (min-width: 800px) and (min-height: 420px) {
    display: flex;
    /*position: relative;*/
  }
}

.Theater-selectorSlider {
  @media (min-width: 800px) and (min-height: 420px)  {
    margin: 0 var(--unit-05);
  }
}

.Theater-selectorArrow {
  display: none;
  margin-top: 15px;

  @media (min-width: 800px) and (min-height: 420px) {
    display: block;
  }
}

.Theater-selectorSlider {
  padding: var(--unit-05) 0 var(--unit);
  height: 96px;
  background-color: var(--c-black);

  @media (min-width: 800px) and (min-height: 420px) {
    float: right;
    padding: 0 0 var(--unit);
    width: 228px;
    height: 84px;
    background-color: transparent;
  }
}

.Theater-selectorItem {
}

.Theater-selectorItem:first-child {
  padding-left: var(--unit);

  @media (min-width: 800px) and (min-height: 420px) {
    padding-left: 0;
  }
}

.Theater-selectorItem:last-child {
  padding-right: var(--unit);

  @media (min-width: 800px) and (min-height: 420px) {
    padding-right: 0;
  }
}

.Theater-selectorItem + .Theater-selectorItem {
  margin-left: var(--unit-05);
}

.Theater-selectorItemThumbnail {
  position: relative;
  width: 108px;
  height: 60px;
}

.Theater-selectorItem--isActive .Theater-selectorItemThumbnail::after {
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: block;
  content: '';
  border: 1px solid var(--c-grey-xxlt);
}

.Theater-selector .flickity-page-dots {
  position: absolute;
  right: 0;
  bottom: 9px;
  left: 0;
  line-height: 0;
}

.Theater-selector .flickity-page-dots .dot {
  margin: 0;
  width: 6px;
  height: 6px;
  background-color: var(--c-grey-md);
  opacity: 1;
}

.Theater-selector .flickity-page-dots .dot.is-selected {
  background-color: var(--c-grey-xlt2);
}

.Theater-selector .flickity-page-dots .dot + .dot {
  margin-left: 6px;
}

.Theater-description {
  font-size: 13px;
  line-height: 18px;
}

</style>



// WEBPACK FOOTER //
// Theater.vue?68f4dad8